import { OverlayContainer } from '@angular/cdk/overlay';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import jwtDecode from 'jwt-decode';
import { Subscription } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { AuthService } from './services/auth/auth.service';
import { TokenService } from './services/auth/token.service';
import { STORAGE_KEYS } from './services/storage/storage.service';
import { ThemeService } from './services/theme/theme.service';
import * as AuthActions from './store/actions/auth.action';
import { TokenDetails } from './store/states/auth.state';
import {
  selectTokenDetails,
  selectUserTenants,
} from './store/selectors/auth.selector';
import { concatLatestFrom } from '@ngrx/effects';
import { Unsuscribe } from './utils/unsuscribe';

@Component({
  selector: 'cube-app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent extends Unsuscribe implements OnInit, OnDestroy {
  isDarkTheme: boolean;
  private subscriptions: Subscription[] = [];

  constructor(
    translate: TranslateService,
    private overlayContainer: OverlayContainer,
    private themeService: ThemeService,
    private store: Store,
    private authService: AuthService,
    private tokenService: TokenService
  ) // private cubeService: CubeService
  {
    super();
    this.store.dispatch(AuthActions.Login());
    this.authService.token
      .pipe(filter((token: string | undefined): token is string => !!token))
      .subscribe((token) => {
        const uniqueId = this.tokenService.getUniqueId(token);
        this.store.dispatch(
          AuthActions.SetTokenDetails({
            tokenDetails: { ...jwtDecode(token), uniqueId } as TokenDetails,
          })
        );
      });
    // this language will be used as a fallback when a translation isn't found in the current language
    translate.setDefaultLang('en');
    // a cosa serve?
    // this.title.setTitle(environment.SOLUTION === Solutions.FLEET40 ? 'RINA FLEET' : 'CUBE - OPTIMUM');

    // the lang to use, if the lang isn't available, it will use the current loader to get them
    localStorage.setItem(
      STORAGE_KEYS.localTimeFormat,
      translate.getBrowserLang()
    );
    const browserLang = translate.getBrowserLang();
    translate.use(browserLang.match(/en|it/) ? browserLang : 'en');
    this.store
      .select(selectTokenDetails)
      .pipe(
        filter(
          (
            tokenDetails: TokenDetails | undefined
          ): tokenDetails is TokenDetails => !!tokenDetails
        ),
        concatLatestFrom(() => this.store.select(selectUserTenants)),
        takeUntil(this.destroy)
      )
      .subscribe({
        next: ([tokenDetails, tenants]) => {
          if (!tenants.length) {
            this.store.dispatch(
              AuthActions.GetUserTenants({ uniqueId: tokenDetails.uniqueId })
            );
          }
        },
      });
  }

  getRouteAnimation(outlet): void {
    return outlet.activatedRouteData.animation;
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.themeService.isDarkTheme.subscribe((isDark) => {
        this.isDarkTheme = isDark;
        this.overlayContainer
          .getContainerElement()
          .classList.remove(isDark ? 'light-theme' : 'dark-theme');
        this.overlayContainer
          .getContainerElement()
          .classList.add(!isDark ? 'light-theme' : 'dark-theme');
      })
    );

    // fromEvent(window, 'storage')
    //   .pipe(filter((ev: StorageEvent) => ev.key === STORAGE_KEYS.currentTenant))
    //   .subscribe((storageEvent: StorageEvent) => {
    //     this.cubeService.cleanCache();
    //     window.location.reload();
    //   });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }
}
